// The wire contract between the authoritative server (src/server) and any client (2b's lobby UI +
// snapshot renderer, and eventually the predicting client of stage 3). Both sides import this file and
// nothing else to agree on message shapes — it is deliberately the one place that changes when the
// protocol changes.
//
// Stage 2 has NO client prediction: the client sends commands and renders whatever snapshot arrives.
// A few fields below exist only so stage 3 (prediction, interpolation, lag compensation) doesn't need a
// protocol version bump to add them later; each is called out where it appears.

import type { Team } from '../core/layout.js';
import type { HeroKind } from '../core/heroes.js';
import type { Command, ArtifactLine, RelicLine } from '../core/commands.js';
import type { HeroId, GameEvent, Vec2 } from '../core/events.js';
import type { MonsterId, MonsterKind, MonsterState } from '../core/monsters.js';
import type { SummonId, SummonKind } from '../core/summons.js';
import type { MatchStats, HeroStatLine } from '../core/stats.js';

export type { Team, HeroKind, Command, HeroId, GameEvent, Vec2, ArtifactLine, RelicLine, MonsterId, MonsterKind, SummonId, SummonKind, MatchStats, HeroStatLine };

export const PROTOCOL_VERSION = 24;  // 5: matchEnd carries `stats`; 6: hero passives (`stats.passive`, `you.effects.stillFor`); 7: matchEnd `replayId`; 8: pre-match countdown (`matchStart.countdown`, snapshot/roomState `countdown`, negative `pong.tick`); 9: «Астрал» heroes (hero `invisible`/`forced`/`confused`/`sleepless`, state 'flock', `you.effects.forced`); 10: team-shared ink (`you.ink` is the seat's TEAM pool, matchEnd stats `inkSpent` replaces `inkEarned`); 11: totems replace the bell (snapshot `totems` for `bells`, attack target `{ totem }`, artefact lines `totemWard`/`totemWrath` for `bell`, passive lines `hourglass`/`mistAmulet`, `num` event `miss`); 12: Сапоги гонца only out of combat (`you.effects.quietFor`); 13: passive artefact lines `graveCandle`/`dragonTooth`/`banner`; 14: relics (snapshot `relics`, `you.relicCd`/`you.eyeFor`, cast key 'T', command `buyRelic`, event `relic`, matchEnd stats `relics`); 15: own-hero prediction state (`you.atk`, `you.effects.kb`/`wallShadow`), roomState `seed`; 16: matchEnd stats `relics` per team is the buy-order list `{ line, lost }[]`, not counters; 17: Без отражения's silhouette (hero `silhouette`, `you.effects.stillFor` for Тень too); 18: «Заклинатели» — summoned creatures (snapshot/replay `summons`, attack/cast target `{ summon }`, `you.atk.pending.summon`, event `summonNum`, fx `summon`/`summonGone`/`tether`/`wolfForm`), hero `wolfFor`/`tethered`, `you.effects.tether`; 19: Связка replaces Нити (hero `tether: { to? }` for `tethered`, `you.effects.tether` `{ to?, max, left }`, fx `bind`); 20: Сокол на перчатке's dive arc (projectile snapshot `arc`); 21: an unnamed player's seat `name` is '' (the client words it in its own language), not the server's Russian default; 22: fx `bindThrow` (Связка's throw from the Кукольник's hand, gated on him); 23: fx `hit` carries `by` — the attacker's kind (hero, summon or monster; absent when nobody swung), so the impact reads as whose blow it was; 24: fx `ramHit`/`leapLand`/`flockLand`/`shadowBurst` replace the generic `shockwave` (with `slam`, and Прыжок's `splat`) on Таран, Прыжок, Стая and Тень на стене — fx `slam` and `shockwave` are no longer sent, and Упор's charged hit sends no `shockwave` beside its (big) `hit`; Сделка, Облик Волчицы and Ветряное масло no longer send a `buff` beside their own event

export type PlayerId = string;
export type RoomId = string;
export type SeatIndex = 0 | 1 | 2 | 3;

/* Rooms are created as `crypto.randomUUID().slice(0, 8)` (server/lobby.ts) — lowercase hex, always 8
   characters — but this is the one place both an invite link (client/net/inviteLink.ts, embedding it in a
   URL) and the wire validator (server/validate.ts) agree on what a well-formed id even looks like, so it's
   defined once here rather than as two separately-maintained regexes that could drift. Deliberately wider
   than the 8-hex-char shape actual ids have: it only needs to reject what could never be a real id (an
   empty string, something absurdly long, URL-hostile characters), not pin down today's exact generator —
   a future generator change (longer ids, say) should not need this touched. */
const ROOM_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
export function isValidRoomId(v: unknown): v is RoomId {
  return typeof v === 'string' && ROOM_ID_PATTERN.test(v);
}

/* A player key is 128 random bits, base64url without padding — generated by the client once per browser. */
const PLAYER_KEY_PATTERN = /^[A-Za-z0-9_-]{22}$/;
export function isValidPlayerKey(v: unknown): v is string {
  return typeof v === 'string' && PLAYER_KEY_PATTERN.test(v);
}

/* ---------------- lobby / room state ---------------- */

export type SeatKind = 'empty' | 'bot' | 'human';

export type SeatView = {
  index: SeatIndex;
  team: Team;
  hero: HeroKind | null;
  kind: SeatKind;
  ready: boolean;      // a bot seat is always true
  connected: boolean;  // human seats only: false while the seat is in its reconnect grace period
  /** Display name, rendered in every OTHER player's browser — hostile input, not a trusted label.
      Trimmed, inner whitespace collapsed to single spaces, capped at 24 characters, control characters
      stripped, empty (after all of that) stays '' — the client shows its own word for a nameless player; it escapes it on render same as
      any other untrusted string. The server re-normalises regardless of what a client's `hello.name` sent,
      so this is always already-clean by the time it reaches here. `null` for a bot or an empty seat. */
  name: string | null;
};

/* What the public room list shows — enough to pick a room to join, nothing seat-owner-only. */
export type RoomSummary = { id: RoomId; seats: SeatView[]; creatorName: string /* fixed at creation; does not change if they leave */ };

/* What members of a room see: the same seats, plus which one (if any) is theirs, plus — once a match is
   running — the last command seq the server has applied for that seat. A reconnecting client MUST resume
   its own seq counter from `yourAckSeq + 1`: the seat's high-water mark survives the disconnect, so an
   old seq is silently dropped as a stale duplicate (see match.ts's dedup) and the seat is uncontrollable
   until the new counter catches back up to it. `countdown`: seconds of the pre-match countdown still to
   run, present only while it runs — a client reconnecting mid-countdown shows the remainder.
   `seed`: the match's seed, present whenever `yourAckSeq` is — a client resuming mid-match builds its prediction
   shadow (net/predictedGame.ts) from it, and core derives each tick's team update order from it (game.ts's
   teamBFirst), so a wrong seed resolves every other tick's collisions in the other order. */
export type RoomState = RoomSummary & { yourSeat: SeatIndex | null; yourAckSeq: number | null; countdown?: number; seed?: number };

export type MatchRosterEntry = { seat: SeatIndex; team: Team; hero: HeroKind };

/* ---------------- client -> server ---------------- */

/* A command tagged with the sequence number and CLIENT tick it was issued on. `tick` is not acted on
   in stage 2 beyond being stored on the applied-command record — stage 3's input buffer and lag
   compensation need it, this just avoids adding it to the shape later. */
export type TimedCommand = { seq: number; tick: number; cmd: Command };

export type ClientMessage =
  /* Must be the first message on every connection. `token` resumes a prior session (see 'welcome')
     within its grace period; omitted or unknown/expired starts a fresh one. `version` must equal
     PROTOCOL_VERSION — the server rejects a mismatch with PROTOCOL_MISMATCH and closes the connection,
     since stage 3 will change message shapes and an old client silently desyncing is worse than a
     visible "please reload" error. */
  /** `name`: already normalised client-side (trim/collapse/cap-24/strip-control, per SeatView's own
      comment) — sent purely so a slow/laggy server round-trip isn't needed before the local seat shows
      the right name. The server re-normalises this from scratch regardless, since a client is free to
      send anything (or a hand-written client, nothing at all): normalisation is never trusted, only
      convenient to have already done. Omitted or empty-after-normalising becomes '' (nameless, worded by each
      client) same as re-joining without one. */
  /** `mapHash`: hashMap() of the map this client was built with (core/map/active.ts's MAP_HASH). It must
      equal the server's — a mismatch is rejected with MAP_MISMATCH and the connection closed, like a
      version mismatch: the client is stale and would render (and predict on) the wrong terrain. */
  /** `playerKey`: a random per-browser id (client/net/playerKey.ts) — what the server's replay store knows
      a player by across sessions, so a player can list their own replays. Not a credential for anything
      else; the server stores only an HMAC of it. */
  | { type: 'hello'; token?: string; version: number; mapHash: string; name?: string; playerKey?: string }
  | { type: 'listRooms' }
  | { type: 'createRoom' }
  | { type: 'joinRoom'; roomId: RoomId }
  | { type: 'leaveRoom' }
  | { type: 'pickSeat'; seat: SeatIndex }
  | { type: 'pickTeam'; team: Team }
  | { type: 'pickHero'; hero: HeroKind }
  | { type: 'setBot'; seat: SeatIndex; bot: boolean }
  | { type: 'ready'; ready: boolean }
  /* Clock sync: server echoes `t` back with its current tick so the client can estimate RTT and how
     far ahead of the server its own clock/prediction should run. Wired by net/connection.ts today only
     to compute an RTT estimate nothing yet reads — stage 3's prediction is the actual consumer. */
  | { type: 'ping'; t: number }
  /* Commands travel in small batches, not one per packet: the client may re-send the last few unacked
     ones alongside new ones for redundancy against packet loss. The server applies each `seq` exactly
     once (tracks the last applied seq per connection, drops duplicates and anything older/equal). */
  | { type: 'cmd'; commands: TimedCommand[] }
  /* Stage 3d: the client's own entity-interpolation delay (net/entityInterp.ts), in ms — how far behind
     its estimate of the live server tick it renders every OTHER hero, derived from that seat's own
     measured snapshot-arrival jitter. The server's lag compensation (shared/rewind.ts) wants this on top
     of a shooter's measured latency to know which past tick to rewind a hit-test to; until now it used a
     configured constant (config.ts's placeholder) for every seat alike. Advisory only, exactly like
     everything else a client reports about itself: the server clamps the combined rewind at its existing
     fairness cap (config.ts's `maxRewindMs`) regardless of what a seat sends here, so misreporting this
     (high or low) only ever costs that seat its own hit-registration fairness, never anyone else's. */
  | { type: 'interpDelay'; ms: number }
  /* Echoes a `latencyPing`'s `seq` unchanged, the instant it arrives — see that type's own comment. Kept
     out of the rate-limited command path (server/rateLimit.ts) and out of any client-side batching: a pong
     delayed behind this client's own send queue would inflate ITS OWN measured latency, which is the wrong
     direction both for that seat's lag compensation and for whoever is shooting at it. */
  | { type: 'latencyPong'; seq: number };

/* ---------------- server -> client ---------------- */

export type ErrorCode =
  | 'BAD_MESSAGE' | 'HELLO_REQUIRED' | 'PROTOCOL_MISMATCH' | 'MAP_MISMATCH'
  | 'ROOM_NOT_FOUND' | 'ROOM_FULL' | 'TOO_MANY_ROOMS' | 'NOT_IN_ROOM' | 'ALREADY_IN_ROOM'
  | 'SEAT_INVALID' | 'SEAT_TAKEN' | 'SEAT_NOT_YOURS' | 'TEAM_FULL'
  | 'HERO_INVALID' | 'HERO_REQUIRED' | 'HERO_TAKEN'
  | 'MATCH_ALREADY_STARTED' | 'MATCH_NOT_ACTIVE' | 'RATE_LIMITED'
  /* Sent to the connection a token takeover just displaced (server.ts's handleHello), right before the
     server closes it — otherwise that side sees an ordinary drop, reconnects with the same (now
     re-taken) token, and displaces the new side right back: a silent, permanent ping-pong between two
     tabs of one browser profile. Terminal on the client (net/connection.ts): no retry follows. */
  | 'SESSION_TAKEN_ELSEWHERE';

/* `moving` is a boolean, not raw path data: core already knows "is this hero currently walking" exactly,
   so it says so directly instead of leaking path/pending-swing fields the client has no other use for.
   Everything that a viewer COUNTS DOWN (a cooldown digit, a respawn timer, a stun/zone/barrier/reveal
   lifetime) is instead sent as remaining SECONDS as of this snapshot, never a raw `core.t`-relative
   timestamp: `tick`/`dt`/`clock` and ping/pong are on the wire, so "the client has no server-synced
   clock" stopped being true, but seconds-remaining-at-snapshot needs no sync either way and stays exactly
   as clock-independent as a boolean — simplest available option twice over. A pure on/off status with
   nothing to count down (slowed, shielded, hasted, enraged, hasHot, empowered, feintArmed, invulnerable,
   taunted, pulled) stays a plain boolean.
   Everything below `lift` is the smallest addition that gets fx.ts's telegraphs()/statuses()/deaths()
   working online (see matchView.ts's header and net/remoteFx.ts): continuous per-tick status flags a
   viewer needs to draw a ring/telegraph, not the full fx.ts feature set (one-shot particle bursts still
   don't run online — those need target-hero refs, def.boltLook already being static/client-known helps,
   but the rest is a materially bigger lift, tracked separately). `taunted`/`pulled` are plain booleans,
   not `taunt.by`/`pull.by`: a tether TARGET's position would leak fog if that hero isn't independently
   visible (the same class of leak lob endpoints needed independent gating for), but "is this hero
   tethered at all" leaks nothing and is enough to restore the visual. The full hots/haste particle
   trigger cadence (`hots`/buff magnitudes) is still left out — `hasHot`/`hasted` are presence booleans
   only, enough for a status ring, not the periodic mote spawn fx.ts does with them.

   Artefacts (docs/game-design.md, "Артефакты" — the flat, single-tier shop: one price, no upgrades) add a
   few more fields, all on every hero the usual visibility rule already lets through — a visible enemy's
   loadout and marks matter as much as your own: a brand is a warning both teams must read (the design
   doc's own "visible to both teams" language for status effects), a passive changes how a fight against
   that hero goes even though it has no button. What stays OWN-hero-only is the artefacts' cooldowns, for
   the same reason `cdQ`/`cdW` below are on `you` and nowhere else: a rival's exact reload timer is real
   MOBAs' usual asymmetry, not something this hero's opponents get to read off the wire. `line` is not
   "your own" information — it identifies what's equipped, which a visible hero already advertises by its
   very presence (the mark you see land) — only "how soon can they do it again" is private. */
export type ArtifactSlotView = { line: ArtifactLine };
export type HeroSnapshot =
  | { id: HeroId; visible: false }
  | {
      id: HeroId; visible: true; team: Team; hero: HeroKind; alive: boolean;
      pos: Vec2; yaw: number; hp: number;
      /** Наплечник adds flat max hp on top of `HEROES[hero].hp` (core's own `maxHpOf()`) — any health bar
          drawn as `hp / HEROES[hero].hp` is wrong for a hero holding it, silently showing a bar that never
          reaches full. Sent for every hero rather than left for the client to recompute from `artifacts`
          below, since "holds Наплечник" is only half the formula (the flat bonus amount is core's own
          constant, not something worth duplicating client-side just to save four bytes on the wire). */
      maxHp: number;
      state: string; moving: boolean; lift: number;
      /** Seconds elapsed since entering the current `state` (core's own `Hero.stateT`) — stage 3's own
          predictedGame.ts is the reader: resyncing mid-windup/recover/hop/cast/channel needs this to place
          the shadow hero at the right POINT in that state, not just the right state, and the timed states
          it drives (hop/leap/feint's lerp fraction, a cast's remaining time) have no other way to recover
          "how far in" a resync lands. */
      stateT: number;
      /** Seconds remaining stunned, 0 when not — replaces a `stunned` boolean so the same field can also
          drive a future countdown; `> 0` is exactly the old boolean's meaning. */
      stunFor: number;
      /** Seconds until respawn, present only while `!alive`. */
      respawnIn?: number;
      slowed: boolean; shielded: boolean; hasted: boolean; enraged: boolean; hasHot: boolean;
      empowered: boolean; feintArmed: boolean; invulnerable: boolean; taunted: boolean; pulled: boolean;
      /** Липкое пламя's afterburn is on this hero (core's `Hero.burn`) — a presence flag for the ember fx. */
      burning: boolean;
      /** Windup/dash/lunge/lungeWindup telegraph: the line ahead of the hero. `mult`/`from` (core's own
          `Dash` carries these too, for lunge's empowered multiplier and its wall-slam fx) never travel:
          predictedGame.ts's shadow's own `g.damage()`/fx calls are never read back (see that module's
          header), so a fabricated placeholder for either cannot leak or double-hit anything real. `hit`
          does NOT get the same treatment: skills.ts's own lunge-stepping code reads `dash.hit.size` to
          decide whether a hit has landed yet at all, to reset Выпад's cooldown for a chain — a fabricated
          empty set silently told the shadow no hit had ever landed, so it kept the follow-up cast locally
          rejected (`canCast` gates on cooldown) for as long as an outage's worth of ticks, exactly the
          latency prediction exists to hide, on the one hero whose whole identity is chaining this. `hit`
          is sent as a plain boolean — "has this dash connected at least once" is all that check needs,
          never which enemies or how many. */
      dash?: { dir: Vec2; left: number; hit?: boolean };
      /** Arquebusier's charge shot: aim direction and fill 0..1, computed server-side (same reasoning as
          `you.charge` below — the client has no server-synced clock to turn a start timestamp into a
          fraction itself in stage 2, so the server sends the fraction directly instead of a `t0`). */
      charge?: { dir: Vec2; k: number };
      /** Devil's leap (and hop/feint generally, core's own `Hop`): the landing circle for every viewer, and
          — since stage 3 — everything predictedGame.ts needs to resume the hero's OWN hop/leap/feint
          mid-flight: `from`/`dur`/`arc` were not previously sent (nothing but the shared fx.ts telegraph
          needed them), so a resync landing mid-hop had no way to reconstruct the lerp `h.pos.lerpVectors
          (hop.from, hop.to, k)` core itself is running — the hero fell back to 'normal' i.e. rendered flat
          on the ground in the walk animation for the whole hop, one full one-way-latency behind. */
      hop?: { from: Vec2; to: Vec2; dur: number; arc?: number };
      /** The current cast's total windup, present only while `state === 'casting'` (every `castThen(...)`
          skill — durer's Q, others). Combined with `stateT` above, predictedGame.ts fabricates a no-op
          resolution closure for its shadow's `Cast.fn` (core's own type; a real one is a closure, which
          cannot travel on a wire in the first place) — the shadow only needs the STATE TRANSITION and
          timing right, never the closure's actual server-decided effect, so a no-op is exactly as harmless
          as everything else predictedGame.ts fabricates for a value it only ever reads back locally. */
      castFor?: number;
      /** Salamander's Q (Огненная струя): aim direction and seconds remaining, present only while
          `state === 'channel'`. `tickAt` (core's own `Channel.tickAt`, its next damage-tick instant) is not
          sent — predictedGame.ts's shadow starts it at the resync instant, harmless for the same reason
          `dash`'s `hit`/`mult`/`from` are: the resulting `g.damage()` calls are never read back. */
      channel?: { dir: Vec2; remaining: number };
      /** Seconds since death as of this snapshot (not a `diedAt` timestamp — same clock-sync reasoning
          as `charge.k` above). Set only once `!alive`; the death-drain animation re-reads it fresh every
          snapshot instead of ticking it locally, same stepiness as everything else here at the snapshot
          rate. */
      deadFor?: number;
      /** E/R loadout — see this type's own header for why `line` travels to everyone who can see the hero
          at all, while the matching cooldown does not. `null` is an empty slot, same shop-can-fill-it
          meaning offline and online. A passive line (no button, no cooldown) still shows up here — its
          effect is silent, but what a visible hero is holding never is. */
      artifacts: { E: ArtifactSlotView | null; R: ArtifactSlotView | null };
      /** Seconds left branded by Клеймо (core's `Hero.marks`, its only source now that Игла гравёра is
          gone), 0 when not — a countdown like `stunFor`, not a plain boolean, because the line's own
          tooltip names a duration. */
      brandFor: number;
      /** Пергаментный плащ (core's `Hero.cloakUntil`): untargetable and unable to act, deliberately its
          own field rather than folded into `invulnerable` — core keeps the two gates separate (see
          `cloakUntil`'s own comment, "distinct from Финт's reuse of invulnUntil"), so a client that only
          watched `invulnerable` would never see a cloaked hero at all. The view needs this to read clearly
          on an enemy: same ghost-contour look the engine already draws for `invulnerable`, plus it is the
          one status that must also gate the local player's own targeting — a click on a cloaked enemy has
          to visibly do nothing rather than silently fail. */
      cloaked: boolean;
      /* ---- «Астрал»: each present only while it holds ---- */
      /** Слиться. Only ever reaches Тень's own team (and a replay's full vision): to the enemy an invisible
          hero is `{ id, visible: false }` like any other hidden one. */
      invisible?: true;
      /** Без отражения: Тень has stood still long enough to be a silhouette — seen by both teams, beyond every
          attack, skill, zone and CC; enemy attack orders on her are dropped. */
      silhouette?: true;
      /** Соблазн (`charm`: walking to Грех) or Стая's fear (`fear`: running home) — no orders, casts or
          attacks; `left` in seconds. Who charmed is not sent: a tether end could sit in fog. */
      forced?: { kind: 'charm' | 'fear'; left: number };
      /** Standing in an enemy Наваждение: its basic attacks may hit its own ally. */
      confused?: true;
      /** Бессонница: an enemy Кошмар is near enough that this hero heals nothing (fountain at half). */
      sleepless?: true;
      /* ---- «Заклинатели»: each present only while it holds ---- */
      /** Обернуться: seconds of wolf form left — a melee basic attack at SKILLS.shewolf.W.range. */
      wolfFor?: number;
      /** Связка: tied, and — only when the receiving team sees it there — to what spot (the other hero, a summon,
          a monster, a tree...). A replay's full-vision world adds `seen` (bit 1 = team a, 2 = team b), never live. */
      tether?: { to?: Vec2; seen?: number };
    };

/* `contested` is deliberately left off the wire: it would tell a team an enemy is standing on a point
   it cannot otherwise see — the same leak fog-of-war exists to prevent. The client derives it itself
   (net/remoteGame.ts's applySnapshot) from whichever heroes it can actually see standing on the point,
   same rule and radius as core's own updatePoints() uses on the real (unfiltered) list. */
export type PointSnapshot = { id: string; owner: Team | null; progress: number };

/* Persistent world entities beyond heroes, filtered by the same rule: your own team's always included,
   an enemy's only if `inSight` at the receiving team — with two deliberate exceptions:
   - Reveals are never sent to the team that doesn't own them: a reveal (ward/flare) grants ITS OWNER's
     vision, so showing its position to the opposing team leaks exactly what fog-of-war exists to hide.
   - Traps are never sent to the enemy team while armed, full stop — not even if in their sight — per
     product decision: a trap's element of surprise is the point (Аркебузир's Отход), so the enemy must
     not see it coming. This needs no separate "reveal once it fires" case to implement: core removes a
     trap from `game.traps` in the very same tick it sets `fired = true` (see game.ts's updateZones), so a
     fired trap is never present in this array for either team to be sent — the momentary reveal at the
     trigger instant already happens through the ordinary `trapPop` fx event, which is position-gated like
     any other ambient effect and needs no trap-specific handling.
   Every entity has a stable `id`, assigned once per underlying object and unchanged across snapshots for
   as long as that object exists — required for a client to interpolate/match an entity across the 20 Hz
   snapshot stream instead of only ever seeing teleporting new instances. */
/** `boltLook` rides along per-projectile rather than being looked up from the shooter's hero kind: a
    projectile snapshot carries no hero reference (team is all it needs for anti-cheat/rendering already),
    and two heroes on the same team can have different bolt looks — without this every online projectile
    is the same sphere regardless of who fired it. */
// `arc`: Сокол на перчатке's (`boltLook === 'falcon'`) 0..1 flight progress, rounded — purely visual (the
// client's climb-and-dive arc height/pitch), never present for any other bolt look; core computes it
// (game.ts's updateProjectiles) and keeps it monotonic so the arc never jitters.
export type ProjectileSnapshot = { id: number; pos: Vec2; team: Team; color?: number; big?: boolean; boltLook?: string; arc?: number };
/* `from`/`to` are independently nulled by their own visibility, not by the lob's as a whole: a lob is
   included once EITHER endpoint is visible, but a hidden endpoint must never appear in the payload just
   because the other one did — that was the exact leak an enemy lobbing at a visible target used to cause
   (their launch position, `from`, leaking despite being in fog). */
export type LobSnapshot = { id: number; from: Vec2 | null; to: Vec2 | null; r: number; t0: number; flight: number; team: Team };
/** `left`/`life` replace a raw `until` timestamp — see HeroSnapshot's header on why remaining-seconds is
    what travels now. `life` is the zone's own total duration (core's `Zone.life`), needed alongside `left`
    to reproduce fx.ts's grow-then-fade curve (`1 - left/life`), which a bare countdown can't derive. */
export type PoolSnapshot = {
  id: number; pos: Vec2; r: number; team: Team; left: number; life: number;
  look?: string; heal?: number; dmg?: number; slow?: number; immune?: boolean; reveal?: boolean;
};
/** `armIn` (seconds until armed, 0 once it is) replaces `armAt`; `left` replaces `until`. */
export type TrapSnapshot = { id: number; pos: Vec2; r: number; team: Team; armIn: number; left: number };
export type BarrierSnapshot = { id: number; center: Vec2; dir: Vec2; half: number; team: Team; left: number };
export type RevealSnapshot = { id: number; x: number; z: number; r: number; left: number };
/** Тотем Стойкости (`ward`) / Тотем Гнева (`wrath`): owner's team always, the enemy only when in sight —
    it is there to be seen and struck. `hp` is hits left (0..2), `left` seconds of life left. */
export type TotemSnapshot = { id: number; kind: 'ward' | 'wrath'; pos: Vec2; r: number; team: Team; hp: number; left: number };

/** Neutral monsters (core's monsters.ts), one per den in `LAYOUT.bosses` order — `id`/`kind` are public
    (the dens are static map data), everything else only while the receiving team has the monster's current
    spot in sight, same rule as a totem. A dead monster follows the SAME rule rather than being hidden: its
    `pos` is where it died, which the team can already see, and "the den is empty" is already public through
    the killer's `teamBuffs` entry — so `alive: false` for a monster in sight tells nobody anything new, and
    lets a team standing at the den see it is empty instead of guessing from a missing model. `target` never
    travels: who a monster is chasing would point at a hero the receiving team may not be able to see.
    Accepted by design: a visible monster's `yaw`/`state`/`moving` (turning, chasing, winding up) still hint
    at the DIRECTION of an unseen attacker — the same thing a player watching it on screen would read anyway,
    and hiding it would mean drawing a monster that visibly ignores being hit. */
export type MonsterSnapshot =
  | { id: MonsterId; kind: MonsterKind; visible: false }
  | {
      id: MonsterId; kind: MonsterKind; visible: true; alive: boolean;
      pos: Vec2; yaw: number; hp: number; maxHp: number; state: MonsterState; stateT: number; moving: boolean;
      /** Seconds left branded by Клеймо, 0 when not — same meaning as HeroSnapshot's `brandFor`. */
      brandFor: number;
    };

/** A summoned creature (core's summons.ts) — a LIST of the ones the receiving team can see, like pools: its own
    team's always, an enemy's only under the same fog/grass rule as a totem. `id` is core's own, stable for the
    creature's whole life and never reused, so a client matches it across snapshots and targets it by it.
    `owner` is a hero id only (the creature kinds are unique per hero anyway). `left`/`life` are its lifetime,
    `swingAgo` seconds since its last attack started (capped at 9), for the view. */
export type SummonSnapshot = {
  id: SummonId; kind: SummonKind; team: Team; owner: HeroId;
  pos: Vec2; yaw: number; hp: number; maxHp: number; left: number; life: number; moving: boolean; swingAgo: number;
  /** Seconds left branded by Клеймо, 0 when not. */
  brandFor: number;
};

/** Seconds left on each monster buff per team, `null` when that team does not hold it. Both teams' buffs go
    to every seat: who holds Бегемот/Грифон is scoreboard information in the design doc, not a fog secret. */
export type TeamBuffsSnapshot = Record<Team, { behemoth: number | null; griffin: number | null }>;

/** The relic each team holds, `null` for none — sent to every seat whatever the fog: a relic is a public prize
    (bought and lost in front of everyone, drawn on its holders' plates so it can be hunted). Its cooldown and
    an open Око stay the holding team's own (`you.relicCd`/`you.eyeFor`). */
export type RelicsSnapshot = Record<Team, RelicLine | null>;

export type SnapshotMessage = {
  type: 'snapshot';
  /* The server sim tick (STEP_DT units) this snapshot reflects, and the fixed step duration itself —
     the tick-rate contract a predicting client needs to run its own clock against. */
  tick: number;
  dt: number;
  /* Snapshot identity for future delta compression: `id` is this snapshot's own id (== tick for now),
     `baseline` is the id of the snapshot it is a delta against, or null for a full snapshot. Stage 2
     always sends full snapshots (baseline: null) — the field exists so deltas can be added later
     without a protocol version bump. */
  id: number;
  baseline: number | null;
  /* The last command `seq` from THIS connection's own hero that the server had applied as of this
     tick — what stage 3's reconciliation replays from. */
  ackSeq: number;
  clock: number;
  /* Seconds of the pre-match countdown left (core's RULES.countdown at matchStart), present only while it
     runs. Countdown snapshots all carry `tick: 0` — the game is not stepped until it reaches zero. */
  countdown?: number;
  score: { a: number; b: number };
  overtime: boolean;
  winner: Team | null;
  points: PointSnapshot[];
  /* Every hero in seat order. A hero the receiving team cannot see appears only as `{ id, visible: false }`
     — never with a position: this is the anti-cheat line, the same rule enforced on commands. */
  heroes: HeroSnapshot[];
  projectiles: ProjectileSnapshot[];
  lobs: LobSnapshot[];
  pools: PoolSnapshot[];
  traps: TrapSnapshot[];
  barriers: BarrierSnapshot[];
  reveals: RevealSnapshot[];
  totems: TotemSnapshot[];
  monsters: MonsterSnapshot[];
  summons: SummonSnapshot[];
  teamBuffs: TeamBuffsSnapshot;
  /** Absent from a pre-protocol-14 snapshot: read as no relics anywhere. */
  relics?: RelicsSnapshot;
  /* fx/num/anim events, filtered to what the receiving team may see (same visibleTo/inSight rule). */
  events: GameEvent[];
  /* Your own hero's Q/W cooldown seconds remaining and charge-skill fill level (0..1, null when not
     mid-charge) — player-specific, so it lives here rather than as a per-hero snapshot field, which
     would otherwise mean leaking a rival's exact cooldowns, real MOBAs' usual information asymmetry.

     Artefacts add: `ink` (core's `Game.ink[team]` — the seat's TEAM pool, shared with the teammate, never the enemy's),
     `inBase` (core's `RULES.fountainRadius` check `buyArtifact` already gates on — sent raw so the shop UI,
     a separate task, decides for itself when to grey out buy/sell rather than trusting a pre-computed
     boolean this task would have to guess the shape of), `artifactCdFor` keyed by slot (the private half of
     `artifacts` above — line/tier already came with the hero's own entry in `heroes[]`, so only the
     cooldown needs repeating here), and `artifactLocks`: this player's TEAM's active 45s per-line locks
     (core's `ArtifactLock`), remaining seconds, needed to grey out a line mid-lock — never sent for the
     enemy team, whose locks are their own business and no one else's. Deliberately NOT sent: shop
     affordance booleans (canBuy/canAfford/canSell per slot) — the shop screen is a separate, not-yet-built
     task; this sends the raw ingredients (`ink`, `inBase`, tier costs importable from core's own `SHOP`
     constant, the lock list) and lets that screen decide, rather than this task guessing its exact needs. */
  you: {
    cdQ: number; cdW: number; charge: number | null;
    ink: number; inBase: boolean;
    artifactCdFor: { E: number; R: number };
    artifactLocks: { line: ArtifactLine; remaining: number }[];
    effects: YouEffects;
    /** This seat's TEAM's relic cooldown, seconds (0 when ready or none held), and seconds left of the team's
        open Око Бестиария (0 when closed). Absent from a pre-protocol-14 snapshot: read as 0. */
    relicCd?: number;
    eyeFor?: number;
    /** The own basic attack's rhythm, so the prediction swings when the server does instead of restarting
        a swing on every replay: `cd` seconds until the next swing may start, `flip` which of the hero's
        swing clips plays next (core's `clipFlip`), `swingAgo` seconds since the last swing started (capped),
        and `pending` a swing already started whose hit lands in `in` seconds, on a hero, monster or totem
        by id. Absent from a pre-protocol-15 snapshot. */
    atk?: {
      cd: number; flip: number; swingAgo: number;
      pending: { in: number; hero?: HeroId; monster?: MonsterId; summon?: SummonId; totem?: number } | null;
    };
  };
};

/* Whatever on your own hero changes how it moves or what it may do next — so the client's prediction
   (net/predictedGame.ts) replays at the speed and under the constraints the server really applies, instead
   of walking a slowed hero at full speed and being pulled back on every snapshot. Seconds left, like
   HeroSnapshot's `stunFor`. Only your own: `taunt.by` is a hero id, never a position, so it leaks nothing
   fog hides (the prediction only uses the taunter's position when that hero is visible anyway). */
export type YouEffects = {
  slows: { pct: number; left: number }[];
  haste: { val: number; left: number }[];
  taunt: { by: HeroId; left: number } | null;
  /** Крюк: dragged from `from` to `to` over `dur` seconds, `elapsed` of them already. */
  pull: { from: Vec2; to: Vec2; dur: number; elapsed: number } | null;
  cloakFor: number;
  /** Соблазн / Стая's fear on your own hero: the server walks it, your orders are ignored. `by` is an id only. */
  forced?: { kind: 'charm' | 'fear'; by: HeroId; left: number } | null;
  /** Сошка (arquebusier) / Без отражения (Тень): seconds stood still, capped at that passive's threshold — past
      it the extra attack range is on / she is a silhouette, and prediction must know which. */
  stillFor?: number;
  /** Сапоги гонца (sent only while held): seconds since the hero last took damage, capped at the boots'
      `quiet` — reaching it means the speed bonus is on. Absent = out of combat. */
  quietFor?: number;
  /** Knockback velocity still sliding the hero (core's `Hero.kb`), sent only while it moves it at all. */
  kb?: Vec2;
  /** Тень на стене: the mark she swaps to in `left` seconds (core's `Hero.wallShadow`). */
  wallShadow?: { pos: Vec2; left: number };
  /** Связка on your own hero: no further than `max` from `to` (the other end) for `left` seconds; `max` only
      shrinks. `to` only while your team sees that end: without it the prediction leaves the bond to the server.
      `mate`: the other end is that enemy hero (sent with `to` only) — the pair splits a correction in half. */
  tether?: { to?: Vec2; mate?: HeroId; max: number; left: number };
};

export type ServerMessage =
  | { type: 'welcome'; playerId: PlayerId; token: string; resumed: boolean; version: number }
  | { type: 'roomList'; rooms: RoomSummary[] }
  | { type: 'roomState'; room: RoomState }
  | { type: 'roomClosed'; roomId: RoomId }
  /* `countdown`: seconds before tick 1 is simulated. Until then the server ignores commands and does not
     step the game; the client shows the countdown and sends nothing. */
  | { type: 'matchStart'; roomId: RoomId; seed: number; roster: MatchRosterEntry[]; yourHero: HeroId | null; countdown: number }
  | SnapshotMessage
  /* `stats`: both teams in full — the match is over, so nothing in it is a fog secret any more. `name` is
     each human seat's display name at the end (hostile input: escape on render), null for a bot seat. */
  /* `replayId`: present when the server recorded this match; the replay is written right after this
     message, so GET /api/replays/:id may 404 for a moment (or for good, if the write failed). */
  | { type: 'matchEnd'; winner: Team; score: { a: number; b: number }; stats: MatchStats; replayId?: string }
  /* `tick` runs on through the pre-match countdown: it is negative then (−ticks left), reaching 0 exactly
     when the first real tick is about to be simulated — so a clock synced during the countdown needs no
     jump when play starts. It may be fractional (the server's position within its current tick). */
  | { type: 'pong'; t: number; tick: number }
  /* Server-initiated, server-timed one-way latency measurement for lag compensation (server/latency.ts) —
     NOT the same measurement as `ping`/`pong` above, and deliberately so: `ping` is client-initiated,
     client-timed, and feeds THIS client's own clockSync/prediction lead, piggybacking on ordinary traffic
     cadence (net/connection.ts sends it every few seconds regardless of what else is happening) — fine for
     that purpose, but structurally incapable of timing a prompt reply, since it never demands one. This
     pair asks the client to reply immediately and carries nothing else to time or trust: `seq` is identity
     only, echoed back unchanged, and the server times both ends itself — there is nothing here a client
     could lie about. Two measurements of the same round trip from opposite ends is intentional, not
     redundant: do not merge them. */
  | { type: 'latencyPing'; seq: number }
  | { type: 'error'; code: ErrorCode; message?: string };
